import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import {
  ACHIEVEMENTS_TABLE_LOADER,
  REDEMPTION_TABLE_LOADER,
  SHOP_TABLE_LOADER,
  TASKS_TABLE_LOADER,
  achievementsList$,
  setCompanyGamificationAchievements,
  setCompanyGamificationRedemptionList,
  setCompanyGamificationShopItems,
  setCompanyGamificationTasks,
  setGamificationTableLoading,
  setRedemptionBadgeCount,
  setRedemptionItemHistory,
  shopItemsList$,
  tasksList$
} from 'src/store/modules/admin/gamification'

import {
  ACHIEVEMENT_SUCCESS_SAVE_MESSAGE,
  EXPORT_REDEMPTION_GRID,
  GET_REDEMPTION_BADGE_COUNT,
  SHOP_ITEM_SUCCESS_SAVE_MESSAGE
} from 'src/constants'
import company from 'src/services/@core/API/routes/company'
import { showSuccessModal } from 'src/services/dialogs'
import { updateAchievementsHierarchy } from 'src/utils/DataTrees'
import { exportReport } from 'src/utils/Functions'

export const getCompanyAchievementsList = (config) => async (dispatch) => {
  dispatch(
    setGamificationTableLoading({
      loader: ACHIEVEMENTS_TABLE_LOADER,
      flag: true
    })
  )

  try {
    const { items } = await company.getCompanyAchievementsList(config)

    return dispatch(setCompanyGamificationAchievements(items))
  } catch (err) {
    catchError(err)
  } finally {
    dispatch(
      setGamificationTableLoading({
        loader: ACHIEVEMENTS_TABLE_LOADER,
        flag: false
      })
    )
  }
}

export const updateAchievement =
  (config, closeModal) => async (dispatch, getState) => {
    const { hardCurrency, softCurrency } = config
    dispatch(
      setGamificationTableLoading({
        loader: ACHIEVEMENTS_TABLE_LOADER,
        flag: true
      })
    )

    try {
      const updAchievement = await company.updateAchievement({
        ...config,
        softCurrency: Number(softCurrency),
        hardCurrency: Number(hardCurrency)
      })
      const updAchievementsList = updateAchievementsHierarchy(
        achievementsList$(getState()),
        updAchievement
      )

      dispatch(setCompanyGamificationAchievements(updAchievementsList))
      showSuccessModal({
        content: ACHIEVEMENT_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false,
        onOk: closeModal
      })
    } catch (err) {
      catchError(err)
    } finally {
      dispatch(
        setGamificationTableLoading({
          loader: ACHIEVEMENTS_TABLE_LOADER,
          flag: false
        })
      )
    }
  }

export const getCompanyTasksList = (config) => async (dispatch) => {
  dispatch(
    setGamificationTableLoading({
      loader: TASKS_TABLE_LOADER,
      flag: true
    })
  )

  try {
    const { items } = await company.getCompanyTasksList(config)

    dispatch(setCompanyGamificationTasks(items))
  } catch (err) {
    catchError(err)
  } finally {
    dispatch(
      setGamificationTableLoading({
        loader: TASKS_TABLE_LOADER,
        flag: false
      })
    )
  }
}

export const updateTaskCurrency = (config) => async (dispatch, getState) => {
  dispatch(
    setGamificationTableLoading({
      loader: TASKS_TABLE_LOADER,
      flag: true
    })
  )

  try {
    const updTask = await company.updateTask(config)

    const updTasksList = tasksList$(getState()).map((task) =>
      task.uuid === updTask.uuid ? updTask : task
    )

    dispatch(setCompanyGamificationTasks(updTasksList))
  } catch (err) {
    catchError(err)
  } finally {
    dispatch(
      setGamificationTableLoading({
        loader: TASKS_TABLE_LOADER,
        flag: false
      })
    )
  }
}

export const getCompanyShopItemsList = (config) => async (dispatch) => {
  dispatch(
    setGamificationTableLoading({
      loader: SHOP_TABLE_LOADER,
      flag: true
    })
  )

  try {
    const { items = [], meta = {} } = await company.getCompanyShopItemsList(
      config
    )

    dispatch(setCompanyGamificationShopItems(items))
    return meta
  } catch (err) {
    catchError(err)
    return err
  } finally {
    dispatch(
      setGamificationTableLoading({
        loader: SHOP_TABLE_LOADER,
        flag: false
      })
    )
  }
}

export const updateShopItemCurrency =
  (config, closeModal) => async (dispatch, getState) => {
    dispatch(
      setGamificationTableLoading({
        loader: SHOP_TABLE_LOADER,
        flag: true
      })
    )

    try {
      const updShopItem = await company.updateShopItem(config)
      const updShopItemsList = shopItemsList$(getState()).map((shItm) =>
        shItm.uuid === updShopItem.uuid ? updShopItem : shItm
      )

      dispatch(setCompanyGamificationShopItems(updShopItemsList))
      showSuccessModal({
        content: SHOP_ITEM_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false,
        onOk: closeModal
      })
    } catch (err) {
      catchError(err)
    } finally {
      dispatch(
        setGamificationTableLoading({
          loader: SHOP_TABLE_LOADER,
          flag: false
        })
      )
    }
  }

export const getCompanyGamificationRedemptionList =
  (config) => async (dispatch) => {
    dispatch(
      setGamificationTableLoading({
        loader: REDEMPTION_TABLE_LOADER,
        flag: true
      })
    )

    try {
      const { items = [], meta = {} } =
        await company.getCompanyGamificationRedemptionList(config)
      dispatch(setCompanyGamificationRedemptionList(items))
      return meta
    } catch (err) {
      catchError(err)
    } finally {
      dispatch(
        setGamificationTableLoading({
          loader: REDEMPTION_TABLE_LOADER,
          flag: false
        })
      )
    }
  }

export const updateRedemptionItemStatus = (config) => async (dispatch) => {
  dispatch(
    setGamificationTableLoading({
      loader: REDEMPTION_TABLE_LOADER,
      flag: true
    })
  )

  try {
    await company.updateRedemptionItemStatus(config)
  } catch (err) {
    catchError(err)
  } finally {
    dispatch(
      setGamificationTableLoading({
        loader: REDEMPTION_TABLE_LOADER,
        flag: false
      })
    )
  }
}

export const getRedemptionItemHistory = (orderUuid) => async (dispatch) => {
  const { items } = await company.getRedemptionItemHistory({
    params: { orderUuid }
  })

  dispatch(setRedemptionItemHistory(items))
}

export const getRedemptionBadgeCount = () => async (dispatch) => {
  try {
    const { count } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_REDEMPTION_BADGE_COUNT
      }
    })

    dispatch(setRedemptionBadgeCount(count))
  } catch (err) {
    catchError(err)
  }
}

export const exportRedemptionList =
  (config = {}) =>
  async () => {
    const updConfig = {
      pathname: EXPORT_REDEMPTION_GRID,
      ...config
    }
    const fileNameDefault = 'redemption_list.csv'

    await exportReport(updConfig, fileNameDefault)
  }
